import { useEffect, useState } from "react";
import { z } from "zod";
import { AssetSchema } from "@forgepoint/shared";
import type { Asset } from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";

const GetAssetsByIdsResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

/**
 * Loads asset metadata for a list of asset ids in one request.
 * Returns the loaded assets in the order of `assetIds`; ids still loading or not found are left out.
 */
export function useAssetsByIds(assetIds: string[]): Asset[] {
  const [loadedAssets, setLoadedAssets] = useState<Asset[]>([]);
  const idsParam = assetIds.join(",");

  useEffect(() => {
    if (idsParam === "") {
      setLoadedAssets([]);
      return;
    }
    let cancelled = false;
    fetchApi(`api/assets?ids=${idsParam}`, "GET").then((response) => {
      if (cancelled) {
        return;
      }
      const parsed = response.ok
        ? GetAssetsByIdsResponseSchema.safeParse(response.data)
        : null;
      setLoadedAssets(parsed?.success ? parsed.data.assets : []);
    });
    return () => {
      cancelled = true;
    };
  }, [idsParam]);

  const assetsById = new Map(loadedAssets.map((asset) => [asset.id, asset]));
  return assetIds
    .map((id) => assetsById.get(id))
    .filter((asset): asset is Asset => asset !== undefined);
}
